/**
 * Named financial comparison definitions.
 *
 * Metric IDs come from FINANCIAL_METRICS. Do not duplicate statement labels here.
 */

import { FINANCIAL_METRICS } from './financialMetricRegistry';
import { ROIC_WACC_FLOOR_NOTE } from './profitLossBenchmarkConfig';

const CFI_NOT_FCF_WARNING = 'Total CFI includes capex and other investing flows. Do not use CFI as the FCF deduction.';
const FCF_VS_CFI_WARNING = 'This is a capital-allocation context chart, not an FCF formula comparison.';

function metricIdsExist(ids) {
    return ids.every((id) => Boolean(FINANCIAL_METRICS[id]));
}

/**
 * @typedef {Object} FinancialComparison
 * @property {string} id
 * @property {string} label
 * @property {string} category
 * @property {'profitAndLoss' | 'balanceSheet' | 'crossStatement' | 'cashFlow'} [statement]
 * @property {string[]} metricIds
 * @property {string} [derivedMetricId]
 * @property {'bar' | 'line' | 'stackedBar'} chartType
 * @property {string} unit
 * @property {string} description
 * @property {string} investorQuestion
 * @property {string[]} warnings
 * @property {Record<string, string>} [displayTransforms]
 * @property {string} [stackGroup]
 * @property {string[]} [totalMetricIds]
 * @property {boolean} [legacy]
 */

/** @type {FinancialComparison[]} */
export const FINANCIAL_COMPARISONS = [
    {
        id: 'cfo_vs_reported_fcf',
        label: 'CFO vs Free Cash Flow',
        category: 'Owner Earnings',
        metricIds: ['cfo', 'reportedFcf'],
        derivedMetricId: 'fcfConversion',
        chartType: 'bar',
        unit: 'currency',
        description: 'Operating cash remaining after net fixed-asset capex (purchases plus asset-sale proceeds).',
        investorQuestion: 'How much operating cash is left after the business reinvests in its own fixed assets?',
        warnings: [],
    },
    {
        id: 'cfo_vs_gross_capex_fcf',
        label: 'CFO vs FCF Before Asset Sales',
        category: 'Owner Earnings',
        metricIds: ['cfo', 'grossCapexFcf'],
        derivedMetricId: 'grossCapexFcfConversion',
        chartType: 'bar',
        unit: 'currency',
        description: 'Conservative owner-earnings comparison excluding fixed-asset sale proceeds.',
        investorQuestion: 'Does owner earnings still look healthy if asset sales are ignored?',
        warnings: [],
    },
    {
        id: 'cfo_vs_capex',
        label: 'CFO vs Fixed Assets Purchased',
        category: 'Reinvestment',
        metricIds: ['cfo', 'fixedAssetsPurchased'],
        derivedMetricId: 'capexToCfo',
        chartType: 'bar',
        unit: 'currency',
        description: 'How much operating cash is consumed by gross capex.',
        investorQuestion: 'What share of CFO is absorbed by gross fixed-asset purchases?',
        warnings: [],
        displayTransforms: { fixedAssetsPurchased: 'absoluteValue' },
    },
    {
        id: 'fcf_vs_net_profit',
        label: 'Free Cash Flow vs Net Profit',
        category: 'Earnings Quality',
        metricIds: ['reportedFcf', 'netProfit'],
        derivedMetricId: 'fcfToNetProfit',
        chartType: 'bar',
        unit: 'currency',
        description: 'Compare accounting profit with cash remaining after net capex.',
        investorQuestion: 'Is reported profit converting into cash after reinvestment?',
        warnings: [],
    },
    {
        id: 'cfo_vs_net_profit',
        label: 'CFO vs Net Profit',
        category: 'Earnings Quality',
        metricIds: ['cfo', 'netProfit'],
        derivedMetricId: 'cfoToNetProfit',
        chartType: 'bar',
        unit: 'currency',
        description: 'Assess profit-to-cash conversion before capex.',
        investorQuestion: 'How much of net profit shows up as operating cash?',
        warnings: [],
    },
    {
        id: 'capex_vs_depreciation',
        label: 'Fixed Assets Purchased vs Depreciation',
        category: 'Asset Intensity',
        metricIds: ['fixedAssetsPurchased', 'depreciation'],
        derivedMetricId: 'capexToDepreciation',
        chartType: 'bar',
        unit: 'currency',
        description: 'Whether reinvestment is above reported depreciation.',
        investorQuestion: 'Is the company investing more than it depreciates?',
        warnings: [],
        displayTransforms: { fixedAssetsPurchased: 'absoluteValue' },
    },
    {
        id: 'revenue_vs_fcf',
        label: 'Revenue vs Free Cash Flow',
        category: 'Cash Economics',
        metricIds: ['revenue', 'reportedFcf'],
        derivedMetricId: 'fcfMargin',
        chartType: 'bar',
        unit: 'currency',
        description: 'Scale and cash profitability after net capex.',
        investorQuestion: 'How much free cash does each rupee of sales produce?',
        warnings: [],
    },
    {
        id: 'revenue_vs_gross_capex_fcf',
        label: 'Revenue vs FCF Before Asset Sales',
        category: 'Cash Economics',
        metricIds: ['revenue', 'grossCapexFcf'],
        derivedMetricId: 'grossCapexFcfMargin',
        chartType: 'bar',
        unit: 'currency',
        description: 'Conservative cash-profitability trend excluding asset sales.',
        investorQuestion: 'Does cash profitability hold up without asset-sale proceeds?',
        warnings: [],
    },
    {
        id: 'cfo_vs_working_capital',
        label: 'CFO vs Working Capital Changes',
        category: 'Cash Conversion',
        metricIds: ['cfo', 'workingCapitalChanges'],
        chartType: 'bar',
        unit: 'currency',
        description: 'Identify whether working capital supported or absorbed operating cash.',
        investorQuestion: 'Did working capital fund operations or consume cash?',
        warnings: ['Interpret working-capital changes using the native cash-flow sign convention. Positive changes support CFO; negative changes absorb cash.'],
    },
    {
        id: 'working_capital_driver_bridge',
        label: 'Working Capital Cash-Flow Drivers',
        category: 'Cash Conversion',
        metricIds: [
            'receivablesCashFlow',
            'inventoryCashFlow',
            'payablesCashFlow',
            'loansAdvancesCashFlow',
            'otherWorkingCapitalItems',
            'workingCapitalChanges',
        ],
        chartType: 'stackedBar',
        unit: 'currency',
        description: 'Explain the working-capital contribution to CFO using native cash-flow signs.',
        investorQuestion: 'Which working-capital lines released or absorbed cash?',
        warnings: [],
        stackGroup: 'wcDrivers',
        totalMetricIds: ['workingCapitalChanges'],
    },
    {
        id: 'cfi_investing_bridge',
        label: 'CFI Investing Bridge',
        category: 'Capital Allocation',
        metricIds: [
            'cfi',
            'fixedAssetsPurchased',
            'fixedAssetsSold',
            'investmentsPurchased',
            'investmentsSold',
            'interestReceived',
            'dividendsReceived',
            'otherInvestingItems',
        ],
        chartType: 'stackedBar',
        unit: 'currency',
        description: 'Composition of investing cash flow, including capex and financial investments.',
        investorQuestion: 'Where did investing cash actually go?',
        warnings: [CFI_NOT_FCF_WARNING],
        stackGroup: 'cfiComponents',
        totalMetricIds: ['cfi'],
    },
    {
        id: 'fcf_vs_cfi_context',
        label: 'Free Cash Flow vs Investing Cash Flow',
        category: 'Capital Allocation',
        metricIds: ['reportedFcf', 'cfi'],
        chartType: 'bar',
        unit: 'currency',
        description: 'Compare internally generated FCF with total investing deployment. CFI stays signed.',
        investorQuestion: 'Is internally generated FCF large enough relative to total investing outflows?',
        warnings: [FCF_VS_CFI_WARNING],
    },
    {
        id: 'capex_vs_cfi',
        label: 'Fixed Assets Purchased vs CFI',
        category: 'Capital Allocation',
        metricIds: ['fixedAssetsPurchased', 'cfi'],
        chartType: 'bar',
        unit: 'currency',
        description: 'Portion of CFI attributable to fixed-asset reinvestment. Native signs are kept.',
        investorQuestion: 'How much of CFI is actual plant-and-equipment capex versus other investing?',
        warnings: [CFI_NOT_FCF_WARNING],
    },
    {
        id: 'investment_securities_vs_cfi',
        label: 'Investment Securities vs CFI',
        category: 'Capital Allocation',
        metricIds: ['investmentsPurchased', 'investmentsSold', 'cfi'],
        derivedMetricId: 'netInvestmentCashFlow',
        chartType: 'bar',
        unit: 'currency',
        description: 'Identify financial-investment activity within CFI.',
        investorQuestion: 'Is CFI dominated by marketable-security trading rather than operating capex?',
        warnings: [CFI_NOT_FCF_WARNING],
    },
    {
        id: 'fcf_vs_dividends',
        label: 'Free Cash Flow vs Dividends Paid',
        category: 'Shareholder Returns',
        metricIds: ['reportedFcf', 'dividendsPaid'],
        derivedMetricId: 'dividendCoverage',
        chartType: 'bar',
        unit: 'currency',
        description: 'Assess whether dividends are funded by net-capex FCF.',
        investorQuestion: 'Can the dividend be paid from free cash flow?',
        warnings: [],
        displayTransforms: { dividendsPaid: 'absoluteValue' },
    },
    {
        id: 'gross_capex_fcf_vs_dividends',
        label: 'FCF Before Asset Sales vs Dividends Paid',
        category: 'Shareholder Returns',
        metricIds: ['grossCapexFcf', 'dividendsPaid'],
        derivedMetricId: 'grossCapexFcfDividendCoverage',
        chartType: 'bar',
        unit: 'currency',
        description: 'Use conservative FCF to assess dividend safety.',
        investorQuestion: 'Is the dividend still covered if asset sales are excluded from FCF?',
        warnings: [],
        displayTransforms: { dividendsPaid: 'absoluteValue' },
    },
    {
        id: 'fcf_vs_debt_repayment',
        label: 'Free Cash Flow vs Debt Repayment',
        category: 'Balance Sheet',
        metricIds: ['reportedFcf', 'repaymentOfBorrowings'],
        chartType: 'bar',
        unit: 'currency',
        description: 'Assess internal capacity to deleverage.',
        investorQuestion: 'Can FCF cover scheduled or actual debt repayment?',
        warnings: [],
        displayTransforms: { repaymentOfBorrowings: 'absoluteValue' },
    },
    {
        id: 'debt_financing_bridge',
        label: 'Debt Financing Bridge',
        category: 'Balance Sheet',
        metricIds: ['proceedsFromBorrowings', 'repaymentOfBorrowings', 'netDebtCashFlow'],
        chartType: 'stackedBar',
        unit: 'currency',
        description: 'Distinguish debt-funded cash from debt reduction using native signs.',
        investorQuestion: 'Is the company a net borrower or a net debt reducer?',
        warnings: [],
        stackGroup: 'debtFlows',
        totalMetricIds: ['netDebtCashFlow'],
    },
    {
        id: 'cfo_cfi_cff_overview',
        label: 'CFO, CFI and CFF Overview',
        category: 'Cash Flow Overview',
        metricIds: ['cfo', 'cfi', 'cff', 'netCashFlow'],
        chartType: 'bar',
        unit: 'currency',
        description: 'Complete operating, investing, and financing cash-flow profile with native signs.',
        investorQuestion: 'How do the three cash-flow statements add up to net cash flow?',
        warnings: [],
    },
    {
        id: 'cfo_vs_operating_profit',
        label: 'CFO vs Operating Profit',
        category: 'Cash Conversion',
        metricIds: ['cfo', 'operatingProfit'],
        chartType: 'bar',
        unit: 'currency',
        description: 'Compare operating earnings with cash generated by operations. Does not use the raw reported CFO/OP field.',
        investorQuestion: 'Is operating profit converting into operating cash?',
        warnings: [],
    },
    {
        id: 'revenue_vs_net_profit',
        label: 'Revenue vs Net Profit',
        category: 'Growth & Profitability',
        statement: 'profitAndLoss',
        metricIds: ['revenue', 'netProfit'],
        derivedMetricId: 'netProfitMargin',
        chartType: 'bar',
        unit: 'currency',
        description: 'Whether top-line growth is reaching the bottom line.',
        investorQuestion: 'Is profit growing in line with, faster than, or slower than revenue?',
        warnings: [],
    },
    {
        id: 'revenue_vs_operating_profit',
        label: 'Revenue vs Operating Profit',
        category: 'Growth & Profitability',
        statement: 'profitAndLoss',
        metricIds: ['revenue', 'operatingProfit'],
        derivedMetricId: 'operatingProfitMarginPct',
        chartType: 'bar',
        unit: 'currency',
        description: 'Core operating leverage as the business scales.',
        investorQuestion: 'Is the core operating business scaling efficiently as revenue grows?',
        warnings: [],
    },
    {
        id: 'operating_profit_vs_net_profit',
        label: 'Operating Profit vs Net Profit',
        category: 'Earnings Quality',
        statement: 'profitAndLoss',
        metricIds: ['operatingProfit', 'netProfit'],
        chartType: 'bar',
        unit: 'currency',
        description: 'Gap between operating earnings and profit available to shareholders.',
        investorQuestion: 'How much of operating profit is being lost to interest, tax, depreciation, and exceptional items before it reaches shareholders?',
        warnings: [],
    },
    {
        id: 'ebitda_margin_vs_net_profit_margin',
        label: 'EBITDA Margin vs Net Profit Margin',
        category: 'Margin Quality',
        statement: 'profitAndLoss',
        metricIds: ['ebitdaMargin', 'netProfitMargin'],
        chartType: 'line',
        unit: 'percentage',
        description: 'Separates operating-level margin pressure from items below the operating line.',
        investorQuestion: 'Is margin pressure happening at the operating level or below the operating line?',
        warnings: [],
    },
    {
        id: 'interest_expense_vs_operating_profit',
        label: 'Interest Expense vs Operating Profit',
        category: 'Leverage Burden',
        statement: 'profitAndLoss',
        metricIds: ['interestExpense', 'operatingProfit'],
        derivedMetricId: 'interestCoverageEbit',
        chartType: 'bar',
        unit: 'currency',
        description: 'Share of operating profit consumed by interest costs.',
        investorQuestion: 'What share of operating profit is consumed by interest costs?',
        warnings: [],
        displayTransforms: { interestExpense: 'absoluteValue' },
    },
    {
        id: 'net_profit_vs_eps',
        label: 'Net Profit vs EPS',
        category: 'Per-Share Value',
        statement: 'profitAndLoss',
        metricIds: ['netProfit', 'eps'],
        chartType: 'bar',
        unit: 'currency',
        description: 'Dilution check: whether per-share earnings keep pace with reported profit. Use the CAGR window to compare the two series.',
        investorQuestion: 'Is EPS growing as fast as net profit, or is share dilution eroding per-share gains?',
        warnings: ['If EPS CAGR trails net-profit CAGR by 3 percentage points or more over the selected window, share count growth may be eroding per-share gains.'],
    },
    {
        id: 'dividend_payout_vs_net_profit',
        label: 'Net Profit vs Dividend Payout',
        category: 'Capital Allocation',
        statement: 'profitAndLoss',
        metricIds: ['netProfit', 'dividendPayoutRatio'],
        chartType: 'bar',
        unit: 'currency',
        description: 'How the payout ratio moves as reported profit changes.',
        investorQuestion: 'Is the dividend payout policy stable, growing, or shrinking as profit changes?',
        warnings: [],
    },
    {
        id: 'tax_rate_context',
        label: 'Profit Before Tax vs Tax Rate',
        category: 'Tax & Regulatory Context',
        statement: 'profitAndLoss',
        metricIds: ['profitBeforeTax', 'taxRate'],
        chartType: 'bar',
        unit: 'currency',
        description: 'Effective tax rate alongside PBT. A high or low rate is context, not a quality verdict.',
        investorQuestion: 'How has the effective tax rate affected the path from PBT to net profit?',
        warnings: ['Tax rate is not scored. A high or low effective tax rate is not inherently good or bad.'],
    },
    {
        id: 'return_on_equity_vs_return_on_capital_employed',
        label: 'ROE vs ROCE',
        category: 'Capital Efficiency',
        statement: 'profitAndLoss',
        metricIds: ['returnOnEquity', 'returnOnCapitalEmployed'],
        chartType: 'line',
        unit: 'percentage',
        description: 'Compares the return on shareholder equity with the return on all capital employed.',
        investorQuestion: 'Is ROE being amplified by financial leverage relative to the return the whole business earns on all capital employed?',
        warnings: [],
    },
    {
        id: 'roic_vs_wacc',
        label: 'ROIC vs WACC',
        category: 'Value Creation',
        statement: 'profitAndLoss',
        metricIds: ['operatingRoic', 'weightedAverageCostOfCapital'],
        derivedMetricId: 'roicWaccSpread',
        chartType: 'line',
        unit: 'percentage',
        description: 'Whether invested capital earns more than it costs. This is a value-creation chart, not a sector scorecard.',
        investorQuestion: 'Is the business earning more on invested capital than that capital costs?',
        warnings: [ROIC_WACC_FLOOR_NOTE],
    },
    {
        id: 'borrowings_vs_total_equity',
        label: 'Borrowings vs Total Equity',
        category: 'Capital Structure',
        statement: 'balanceSheet',
        metricIds: ['borrowings', 'totalEquity'],
        derivedMetricId: 'debtToEquity',
        chartType: 'bar',
        unit: 'currency',
        description: 'Balance between interest-bearing debt and owner equity (capital plus reserves).',
        investorQuestion: 'How has the balance between debt and shareholder capital evolved?',
        warnings: [],
    },
    {
        id: 'reserves_vs_equity_capital',
        label: 'Reserves vs Equity Capital',
        category: 'Net Worth Composition',
        statement: 'balanceSheet',
        metricIds: ['reserves', 'equityCapital'],
        chartType: 'bar',
        unit: 'currency',
        description: 'How much of net worth was built from retained earnings versus originally paid-in capital. Description-only; not scored.',
        investorQuestion: 'How much of the company\'s net worth was built from retained earnings versus originally paid-in capital?',
        warnings: [],
    },
    {
        id: 'fixed_assets_and_cwip_vs_total_assets',
        label: 'Fixed Assets and CWIP vs Total Assets',
        category: 'Capital Intensity',
        statement: 'balanceSheet',
        metricIds: ['fixedAssets', 'cwip', 'totalAssets'],
        chartType: 'stackedBar',
        unit: 'currency',
        description: 'Capacity already in use (fixed assets) and capacity under construction (CWIP) against the full asset base. Description-only; not scored.',
        investorQuestion: 'Is the company actively expanding capacity (rising CWIP), and how capital-intensive is its asset base overall?',
        warnings: [],
        stackGroup: 'capitalAssets',
        totalMetricIds: ['totalAssets'],
    },
    {
        id: 'working_capital_composition',
        label: 'Working Capital Composition',
        category: 'Working Capital',
        statement: 'balanceSheet',
        metricIds: ['inventoryBalance', 'tradeReceivablesBalance', 'tradePayablesBalance'],
        chartType: 'stackedBar',
        unit: 'currency',
        description: 'Inventory, receivables, and payables together. This is a composition view, not a net working-capital score. A full sector-aware working-capital benchmark, if built, lives in the Balance Sheet Ratios section.',
        investorQuestion: 'How are inventory, receivables, and payables together shaping the company\'s working-capital cycle?',
        warnings: [],
        stackGroup: 'workingCapitalBalances',
    },
    {
        id: 'return_on_equity_vs_return_on_assets',
        label: 'ROE vs ROA',
        category: 'Leverage Effect on Returns',
        statement: 'balanceSheet',
        metricIds: ['returnOnEquity', 'returnOnAssets'],
        chartType: 'line',
        unit: 'percentage',
        description: 'Gap between ROE and ROA as a leverage lens. Complements ROE vs ROCE on the Profit & Loss tab.',
        investorQuestion: 'Is ROE meaningfully higher than ROA, and if so, how much of that gap is financial leverage rather than operating performance?',
        warnings: [],
    },
    {
        id: 'asset_efficiency_trio',
        label: 'Asset, Inventory and Receivables Turnover',
        category: 'Efficiency',
        statement: 'balanceSheet',
        metricIds: ['assetTurnoverRatio', 'inventoryTurnoverRatio', 'receivablesTurnoverRatio'],
        chartType: 'line',
        unit: 'multiple',
        description: 'Whether the company is using assets, inventory, and receivables more or less efficiently over time.',
        investorQuestion: 'Is the company using its assets, inventory, and receivables more or less efficiently over time?',
        warnings: [],
    },
    {
        id: 'net_debt_to_ebitda',
        label: 'Net Debt vs EBITDA',
        category: 'Leverage Capacity',
        statement: 'crossStatement',
        metricIds: ['netDebt', 'ebitda'],
        derivedMetricId: 'netDebtToEbitda',
        chartType: 'bar',
        unit: 'currency',
        description: 'Leverage against earnings capacity: years of EBITDA to repay net debt. Distinct from the Cash Flow tab\'s Debt Financing Bridge, which shows borrowing and repayment cash flows rather than the stock of net debt. Net debt keeps its natural sign (negative for a net-cash company).',
        investorQuestion: 'How many years of EBITDA would it take to fully repay net debt?',
        warnings: [],
    },
    {
        id: 'debt_to_equity_vs_interest_coverage',
        label: 'Debt to Equity vs Interest Coverage',
        category: 'Solvency Cross-Check',
        statement: 'crossStatement',
        metricIds: ['debtToEquity', 'interestCoverageEbit'],
        derivedMetricId: 'interestCoverageEbit',
        chartType: 'bar',
        unit: 'multiple',
        description: 'Whether interest-coverage capacity has kept pace as book leverage has changed.',
        investorQuestion: 'As leverage has changed, has the company\'s ability to comfortably service interest kept pace?',
        warnings: [],
    },
    {
        id: 'dupont_roe_decomposition',
        label: 'DuPont ROE Decomposition',
        category: 'DuPont Analysis',
        statement: 'crossStatement',
        metricIds: ['netProfitMargin', 'assetTurnoverRatio', 'equityMultiplier', 'returnOnEquity'],
        chartType: 'line',
        unit: 'percentage',
        description: 'Splits ROE into net profit margin, asset turnover, and equity multiplier, with reported ROE as a reference line. This is a decomposition view, not an exact algebraic identity guaranteed to multiply out perfectly given real-world rounding and averaging conventions: ROE uses average equity while asset turnover and the equity multiplier may still mix average and ending balances.',
        investorQuestion: 'Is ROE being driven by profitability, asset efficiency, or financial leverage — and has that mix changed over time?',
        warnings: [],
    },
    {
        id: 'sales_vs_earnings',
        label: 'Revenue vs Earnings',
        category: 'Earnings Quality',
        metricIds: ['revenue', 'netProfit', 'operatingProfit'],
        chartType: 'bar',
        unit: 'currency',
        description: 'Legacy comparison opened from the Sales statement row.',
        investorQuestion: 'How do operating profit and net profit track revenue?',
        warnings: [],
        legacy: true,
    },
    {
        id: 'debt_vs_equity_capital',
        label: 'Borrowings vs Equity Capital',
        category: 'Balance Sheet',
        metricIds: ['borrowings', 'equityCapital'],
        chartType: 'bar',
        unit: 'currency',
        description: 'Legacy comparison opened from the debt-equity ratio chart.',
        investorQuestion: 'How do borrowings sit relative to equity capital?',
        warnings: [],
        legacy: true,
    },
];

if (process.env.NODE_ENV !== 'production') {
    FINANCIAL_COMPARISONS.forEach((comparison) => {
        const ids = [
            ...(comparison.metricIds || []),
            ...(comparison.derivedMetricId ? [comparison.derivedMetricId] : []),
        ];
        if (!metricIdsExist(ids)) {
            // eslint-disable-next-line no-console
            console.warn(`Unknown metric ID in comparison ${comparison.id}`, ids.filter((id) => !FINANCIAL_METRICS[id]));
        }
    });
}

export const COMPARISON_BY_ID = FINANCIAL_COMPARISONS.reduce((acc, comparison) => {
    acc[comparison.id] = comparison;
    return acc;
}, {});

/**
 * Map existing ChartModal open keys (API ratio name or financial.{section}.{label})
 * onto named comparison IDs.
 */
export const COMPARISON_BY_RATIO_NAME = {
    fcfAbsolute: 'cfo_vs_reported_fcf',
    'financial.profitAndLoss.Sales': 'sales_vs_earnings',
    debtEquityRatio: 'debt_vs_equity_capital',
};

/**
 * Backward-compatible default export: object keyed by metric / ratio name.
 * Values are stable metric IDs rather than colon-delimited path strings.
 */
const comparisonConfig = {
    fcfAbsolute: ['cfo', 'reportedFcf'],
    'financial.profitAndLoss.Sales': ['netProfit', 'operatingProfit'],
    debtEquityRatio: ['borrowings', 'equityCapital'],
};

export function getComparisonById(comparisonId) {
    return COMPARISON_BY_ID[comparisonId] || null;
}

export function getComparisonForRatioName(ratioName) {
    if (!ratioName) return null;
    const mappedId = COMPARISON_BY_RATIO_NAME[ratioName];
    if (mappedId) return getComparisonById(mappedId);
    return null;
}

/**
 * Grouped react-select options for a tab's Financial Comparisons dropdown.
 * Comparisons without a `statement` tag are treated as cashFlow so existing
 * Cash Flow entries do not need to be rewritten.
 *
 * @param {Array<'cashFlow' | 'profitAndLoss' | 'balanceSheet' | 'crossStatement'>} statementTags
 * @returns {Array<{ label: string, options: Array<{ value: string, label: string }> }>}
 */
export function getFinancialComparisonOptions(statementTags) {
    const allowed = new Set(statementTags || []);
    const groups = new Map();
    FINANCIAL_COMPARISONS.filter((comparison) => !comparison.legacy).forEach((comparison) => {
        const statement = comparison.statement || 'cashFlow';
        if (!allowed.has(statement)) return;
        const category = comparison.category || 'Other';
        if (!groups.has(category)) groups.set(category, []);
        groups.get(category).push({ value: comparison.id, label: comparison.label });
    });
    return Array.from(groups.entries()).map(([label, options]) => ({ label, options }));
}

export default comparisonConfig;
